import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";

// AP2: Fehlergrenze — ein Fehler leert nie die ganze Anwendung.
// Zusätzlich DIAGNOSTISCH: zeigt Fehlermeldung + Komponentenbaum auf der Karte,
// damit ein nur in fremder Umgebung auftretender Fehler ohne DevTools sichtbar
// wird. Der Fehler wird IMMER auch in die Konsole geschrieben (nicht verschluckt).
export class ErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null; stack: string }
> {
  state = { error: null as Error | null, stack: "" };

  static getDerivedStateFromError(error: Error) {
    return { error, stack: "" };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary:", error, info.componentStack);
    this.setState({ stack: info.componentStack ?? "" });
  }

  render() {
    if (this.state.error) {
      const err = this.state.error;
      const firstComponent =
        this.state.stack.trim().split("\n").map((l) => l.trim()).filter(Boolean)[0] ?? "";
      return (
        <div className="mx-auto max-w-3xl p-6">
          <div className="bg-card shadow-card rounded-xl border-soft p-6" style={{ borderWidth: 1 }}>
            <div className="flex items-start gap-3">
              <AlertTriangle size={22} style={{ color: "var(--color-amp-red)" }} className="mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="text-strong text-base font-semibold">Diese Ansicht konnte nicht geladen werden.</div>
                <p className="text-dim mt-1 text-sm">
                  Ein Teil der Anwendung ist auf einen Fehler gelaufen. Bitte laden Sie die Seite neu — und
                  senden Sie uns die folgende Meldung, falls das Problem bestehen bleibt.
                </p>
                {import.meta.env.DEV && (
                  <div className="bg-sunken mt-3 overflow-x-auto rounded-lg p-3">
                    <div className="text-strong font-mono text-xs break-words">{err.message}</div>
                    {firstComponent && (
                      <div className="text-dim mt-1 font-mono text-xs">Komponente: {firstComponent}</div>
                    )}
                    {(err.stack || this.state.stack) && (
                      <pre className="text-faint mt-2 font-mono text-[11px] leading-relaxed whitespace-pre-wrap">
                        {(err.stack ?? "").split("\n").slice(0, 6).join("\n")}
                        {this.state.stack ? "\n— Komponentenbaum —" + this.state.stack.split("\n").slice(0, 6).join("\n") : ""}
                      </pre>
                    )}
                  </div>
                )}
                <button
                  onClick={() => window.location.reload()}
                  className="mt-3 rounded-md px-3 py-1.5 text-sm font-medium text-white"
                  style={{ backgroundColor: "var(--color-brand-600)" }}
                >
                  Seite neu laden
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
